export default class ImageSizer {
  constructor(screenWidth, scale = 1.5) {
    this.isMobile = screenWidth < 768;
    this.screenWidth = screenWidth;
    // Apply mobile-specific scaling (75% of original scale)
    this.scale = this.isMobile ? scale * 0.75 : scale;
    
    // Reference sizes for mobile (375px)
    this.mobileReferenceSizes = [
      { width: 126, height: 372 },
      { width: 250, height: 175 },
      { width: 211, height: 147 },
      { width: 321, height: 170 },
      { width: 145, height: 195 },
      { width: 154, height: 238 },
      { width: 301, height: 195 },
      { width: 259, height: 208 },
      { width: 238, height: 113 }
    ];

    // Reference sizes for desktop (1440px)
    this.desktopReferenceSizes = [
      { width: 444, height: 261 },
      { width: 222, height: 333 },
      { width: 484, height: 339 },
      { width: 393, height: 278 },
      { width: 215, height: 636 },
      { width: 492, height: 319 },
      { width: 465, height: 220 },
      { width: 237, height: 359 },
      { width: 257, height: 347 },
      { width: 444, height: 314 }
    ];

    // Calculate constraints based on reference sizes
    this.calculateConstraints();
  }

  calculateConstraints() {
    const referenceSizes = this.isMobile ? this.mobileReferenceSizes : this.desktopReferenceSizes;
    
    // Calculate aspect ratio ranges
    const aspectRatios = referenceSizes.map(size => size.width / size.height);
    const minAspectRatio = Math.min(...aspectRatios);
    const maxAspectRatio = Math.max(...aspectRatios);
    
    // Calculate size ranges
    const widths = referenceSizes.map(size => size.width);
    const heights = referenceSizes.map(size => size.height);
    
    this.constraints = {
      minWidth: Math.min(...widths),
      maxWidth: Math.max(...widths),
      minHeight: Math.min(...heights),
      maxHeight: Math.max(...heights),
      minAspectRatio,
      maxAspectRatio
    };
  }

  calculateSize(naturalWidth, naturalHeight) {
    const aspectRatio = naturalWidth / naturalHeight;
    let width, height;

    // Target area for visual balance (geometric mean of min/max area)
    const minArea = this.constraints.minWidth * this.constraints.minHeight;
    const maxArea = this.constraints.maxWidth * this.constraints.maxHeight;
    const targetArea = Math.sqrt(minArea * maxArea);

    // Calculate size based on target area and aspect ratio
    height = Math.sqrt(targetArea / aspectRatio);
    width = height * aspectRatio;

    // Apply reduction for square/vertical images
    if (aspectRatio <= 1) {
      width *= 0.85; // Reduce width for square/vertical
      height *= 0.85; // Reduce height for square/vertical
    }

    // Apply scaling
    width *= this.scale;
    height *= this.scale;

    // Clamp to min/max constraints (also scaled)
    width = Math.max(this.constraints.minWidth * this.scale, Math.min(width, this.constraints.maxWidth * this.scale));
    height = Math.max(this.constraints.minHeight * this.scale, Math.min(height, this.constraints.maxHeight * this.scale));

    return { width, height };
  }

  setScale(scale) {
    // Apply mobile-specific scaling when setting scale manually
    this.scale = this.isMobile ? scale * 0.75 : scale;
  }

  getAverageSize() {
    const referenceSizes = this.isMobile ? this.mobileReferenceSizes : this.desktopReferenceSizes;
    const total = referenceSizes.reduce((acc, size) => {
      acc.width += size.width;
      acc.height += size.height;
      return acc;
    }, { width: 0, height: 0 });
    const count = referenceSizes.length;
    return {
      width: (total.width / count) * this.scale,
      height: (total.height / count) * this.scale
    };
  }

  updateScreenWidth(screenWidth) {
    this.isMobile = screenWidth < 768;
    this.screenWidth = screenWidth;
    // Reapply mobile-specific scaling when screen width changes
    const baseScale = this.scale / (this.isMobile ? 0.75 : 1);
    this.scale = this.isMobile ? baseScale * 0.75 : baseScale;
    this.calculateConstraints();
  }
} 